import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { MouseEventHandler } from 'react'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyStayUpgradeBenefitModal from './LoyaltyStayUpgradeBenefitModal'
import LoyaltyTierBenefitMessageBanner from './LoyaltyTierBenefitMessageBanner'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import OfferUrlContext from 'contexts/OfferUrlContext'

const ClickableContainer = styled(Clickable)`
  display: contents;
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  upgradeNotAvailableForSelectedDates?: boolean
}

function LoyaltyStayUpgradeBenefitMessageBanner({ offer, upgradeNotAvailableForSelectedDates = false }: Props) {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const offerUrl = useContext(OfferUrlContext)

  const showRoomUpgradesModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    showModal(<LoyaltyStayUpgradeBenefitModal offer={offer} offerUrl={offerUrl} />)
  }, [showModal, offer, offerUrl])

  const mobileUpgradeText = intl.formatMessage(
    {
      id: 'EZ/TIXE8Ty',
      defaultMessage: 'Free upgrade with your {tierName} status',
      description: 'Mobile banner text indicating a free loyalty upgrade is available for the user tier',
    },
    { tierName: getLoyaltyTierDisplayName(tier) },
  )
  const desktopUpgradeText = intl.formatMessage(
    {
      id: 'VHt5Mm/en8',
      defaultMessage: 'Enjoy a free upgrade with your {tierName} {programName} status',
      description: 'Desktop banner text indicating a free loyalty upgrade is available with tier and program name',
    },
    { tierName: getLoyaltyTierDisplayName(tier), programName: getLoyaltyProgramDisplayName() },
  )
  const unavailableText = intl.formatMessage({
    id: 'fUT+M1cORt',
    defaultMessage: 'Upgrades unavailable on selected dates',
    description: 'Banner text when loyalty upgrades are not available for the selected dates',
  })

  return <ClickableContainer onClick={showRoomUpgradesModal}>
    <LoyaltyTierBenefitMessageBanner
      tier={tier}
      benefitType="hotel_upgrade"
      endContent={<LineInfoCircleIcon />}
      density="compact"
    >
      {
        !upgradeNotAvailableForSelectedDates &&
        <>
          <CSSBreakpoint max="tablet">
            {mobileUpgradeText}
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            {desktopUpgradeText}
          </CSSBreakpoint>
        </>
      }
      {
        upgradeNotAvailableForSelectedDates &&
        <>{unavailableText}</>
      }
    </LoyaltyTierBenefitMessageBanner>
  </ClickableContainer>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(LoyaltyStayUpgradeBenefitMessageBanner)
